import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {AnalyticsParameterValueOperation} from 'graphql-client/__generated/graphqlGlobalTypes';
import AnalyticsParameterValueEditor, {InputProps} from 'insights/parameters/AnalyticsParameterValueEditor';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import {GRAY_DARK, GRAY_LIGHTER} from 'ui-library/Colors';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {Size, TextInputType} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import {assertNever} from 'util/Types';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {
  EMBEDDABLE_TYPE_TO_INSTRUCTION_LABEL,
  EMBEDDABLE_TYPE_TO_LABEL,
  EmbeddableParameterType,
} from '../EmbeddingConstants';

export type ParameterValueTemplateFieldData = Pick<
  AnalyticsParameterValueTemplateGraphQL,
  'valueJson' | 'valueOperation'
>;

export interface ParameterValueTemplateFieldProps<T extends ParameterType> {
  paramType: T;
  embeddingType: EmbeddableParameterType;
  valueTemplate: ParameterValueTemplateFieldData;
  inputProps: Omit<InputProps<T>, 'onChange' | 'valueJson'>;
  onChange: (newValueTemplate: ParameterValueTemplateFieldData) => void;
}

export default function ParameterValueTemplateField<T extends ParameterType>(
  props: ParameterValueTemplateFieldProps<T>
): JSX.Element {
  const {valueOperation, valueJson} = props.valueTemplate;

  const inputField = ((): React.ReactNode => {
    switch (valueOperation) {
      case AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM:
        return (
          <div className={classNames('chipSelectInput', css(styles.useEmbeddingItemInput))}>
            <TextInput
              className={classNames(
                'chipSelectInput-input',
                'u-flex-grow',
                'fieldInput-input',
                'fieldInput-input--small',
                css(styles.useEmbeddingItemInput)
              )}
              disabled={true}
              value={EMBEDDABLE_TYPE_TO_LABEL[props.embeddingType]}
              type={TextInputType.TEXT}
              size={Size.MEDIUM}
            />
          </div>
        );
      case AnalyticsParameterValueOperation.USE_VALUE_JSON:
        return (
          <AnalyticsParameterValueEditor
            paramType={props.paramType}
            inputProps={{
              ...props.inputProps,
              onChange: (newValueJson) => {
                props.onChange({
                  valueOperation: AnalyticsParameterValueOperation.USE_VALUE_JSON,
                  valueJson: newValueJson,
                });
              },
              valueJson,
            }}
          />
        );
      default:
        assertAndContinue(false, `Invalid valueOperation: ${assertNever(valueOperation)}`);
        return null;
    }
  })();

  return (
    <>
      {props.paramType === props.embeddingType && (
        <CheckboxInput
          className={classNames('u-vertical-center-items', css(styles.checkboxRow))}
          checked={valueOperation === AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM}
          label={EMBEDDABLE_TYPE_TO_INSTRUCTION_LABEL[props.embeddingType]}
          id={_.uniqueId('embedding-item-checkbox-')}
          onChange={() => {
            const newValueOperation =
              valueOperation === AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM
                ? AnalyticsParameterValueOperation.USE_VALUE_JSON
                : AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM;

            props.onChange({
              valueOperation: newValueOperation,
              valueJson: null,
            });
          }}
        />
      )}
      {inputField}
    </>
  );
}

const styles = StyleSheet.create({
  checkboxRow: {
    marginBottom: 6,
  },
  useEmbeddingItemInput: {
    backgroundColor: GRAY_LIGHTER,
    color: GRAY_DARK,
  },
});
